/**
 * SVG path data utilities — panel-side mirror of the workbench
 * `tools/rcp-annotator/src/lib/svgPath.ts`. The two must stay in
 * lock-step: identical inputs through identical splitting produce
 * identical primitive IDs, which is the contract `components.json`
 * relies on.
 */

function tokenisePath(d: string): (string | number)[] {
  const out: (string | number)[] = [];
  const re = /([MmLlHhVvCcSsQqTtAaZz])|(-?\d*\.?\d+(?:[eE][+-]?\d+)?)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(d)) !== null) {
    if (m[1] !== undefined) out.push(m[1]);
    else if (m[2] !== undefined) {
      const n = parseFloat(m[2]);
      if (Number.isFinite(n)) out.push(n);
    }
  }
  return out;
}

function commandArity(cmd: string): number {
  switch (cmd.toLowerCase()) {
    case 'm':
    case 'l':
    case 't':
      return 2;
    case 'h':
    case 'v':
      return 1;
    case 'c':
      return 6;
    case 's':
    case 'q':
      return 4;
    case 'a':
      return 7;
    case 'z':
      return 0;
    default:
      return -1;
  }
}

function formatNumber(n: number): string {
  return Number.isInteger(n) ? n.toString() : parseFloat(n.toFixed(4)).toString();
}

export function splitCompoundPath(d: string): string[] {
  const tokens = tokenisePath(d);
  if (tokens.length === 0) return [d];
  if (typeof tokens[0] !== 'string' || tokens[0].toLowerCase() !== 'm') return [d];

  let cmd = '';
  let cx = 0;
  let cy = 0;
  let sx = 0;
  let sy = 0;
  let i = 0;
  const subpaths: string[][] = [];
  let current: string[] = [];

  const flush = (): void => {
    if (current.length > 0) {
      subpaths.push(current);
      current = [];
    }
  };

  while (i < tokens.length) {
    const tok = tokens[i];
    if (typeof tok === 'string') {
      cmd = tok;
      i += 1;
    } else if (cmd === '') {
      break;
    } else if (cmd === 'M') {
      cmd = 'L';
    } else if (cmd === 'm') {
      cmd = 'l';
    }

    const lower = cmd.toLowerCase();
    const isAbs = cmd === cmd.toUpperCase();
    const arity = commandArity(cmd);
    if (arity < 0) break;

    if (lower === 'z') {
      current.push('Z');
      cx = sx;
      cy = sy;
      continue;
    }

    const args: number[] = [];
    for (let k = 0; k < arity; k += 1) {
      const t = tokens[i + k];
      if (typeof t !== 'number') {
        flush();
        return subpaths.length ? subpaths.map((p) => p.join(' ')) : [d];
      }
      args.push(t);
    }
    i += arity;

    // After the validation loop above, `args` holds exactly `arity`
    // finite numbers. Read them through a checked accessor so the strict
    // lint bar is met without silent non-null assertions: an out-of-range
    // read can only be a tokeniser bug, and must fail loudly rather than
    // coerce to undefined and corrupt the emitted path.
    const arg = (k: number): number => {
      const v = args[k];
      if (v === undefined) throw new Error(`svg-path: ${cmd} missing argument ${String(k)}`);
      return v;
    };

    switch (lower) {
      case 'm': {
        flush();
        const x = isAbs ? arg(0) : cx + arg(0);
        const y = isAbs ? arg(1) : cy + arg(1);
        current.push(`M${formatNumber(x)} ${formatNumber(y)}`);
        cx = x;
        cy = y;
        sx = x;
        sy = y;
        break;
      }
      case 'l': {
        const x = isAbs ? arg(0) : cx + arg(0);
        const y = isAbs ? arg(1) : cy + arg(1);
        current.push(`L${formatNumber(x)} ${formatNumber(y)}`);
        cx = x;
        cy = y;
        break;
      }
      case 'h': {
        const x = isAbs ? arg(0) : cx + arg(0);
        current.push(`H${formatNumber(x)}`);
        cx = x;
        break;
      }
      case 'v': {
        const y = isAbs ? arg(0) : cy + arg(0);
        current.push(`V${formatNumber(y)}`);
        cy = y;
        break;
      }
      case 'c': {
        const x1 = isAbs ? arg(0) : cx + arg(0);
        const y1 = isAbs ? arg(1) : cy + arg(1);
        const x2 = isAbs ? arg(2) : cx + arg(2);
        const y2 = isAbs ? arg(3) : cy + arg(3);
        const x = isAbs ? arg(4) : cx + arg(4);
        const y = isAbs ? arg(5) : cy + arg(5);
        current.push(
          `C${formatNumber(x1)} ${formatNumber(y1)} ${formatNumber(x2)} ${formatNumber(y2)} ${formatNumber(x)} ${formatNumber(y)}`,
        );
        cx = x;
        cy = y;
        break;
      }
      case 's': {
        const x2 = isAbs ? arg(0) : cx + arg(0);
        const y2 = isAbs ? arg(1) : cy + arg(1);
        const x = isAbs ? arg(2) : cx + arg(2);
        const y = isAbs ? arg(3) : cy + arg(3);
        current.push(
          `S${formatNumber(x2)} ${formatNumber(y2)} ${formatNumber(x)} ${formatNumber(y)}`,
        );
        cx = x;
        cy = y;
        break;
      }
      case 'q': {
        const x1 = isAbs ? arg(0) : cx + arg(0);
        const y1 = isAbs ? arg(1) : cy + arg(1);
        const x = isAbs ? arg(2) : cx + arg(2);
        const y = isAbs ? arg(3) : cy + arg(3);
        current.push(
          `Q${formatNumber(x1)} ${formatNumber(y1)} ${formatNumber(x)} ${formatNumber(y)}`,
        );
        cx = x;
        cy = y;
        break;
      }
      case 't': {
        const x = isAbs ? arg(0) : cx + arg(0);
        const y = isAbs ? arg(1) : cy + arg(1);
        current.push(`T${formatNumber(x)} ${formatNumber(y)}`);
        cx = x;
        cy = y;
        break;
      }
      case 'a': {
        const x = isAbs ? arg(5) : cx + arg(5);
        const y = isAbs ? arg(6) : cy + arg(6);
        current.push(
          `A${formatNumber(arg(0))} ${formatNumber(arg(1))} ${formatNumber(arg(2))} ${arg(3)} ${arg(4)} ${formatNumber(x)} ${formatNumber(y)}`,
        );
        cx = x;
        cy = y;
        break;
      }
      default:
        break;
    }
  }

  flush();
  if (subpaths.length === 0) return [d];
  return subpaths.map((parts) => parts.join(' '));
}

export function isCompoundPath(d: string): boolean {
  const tokens = tokenisePath(d);
  let count = 0;
  for (const t of tokens) {
    if (typeof t === 'string' && (t === 'M' || t === 'm')) {
      count += 1;
      if (count > 1) return true;
    }
  }
  return false;
}
